import { isNonEmpty } from '@sgme/fp';
import { getBatchAllSolvableFieldIds, getBatchFieldSolveBy } from 'state/batchForm/batchForm.selectors';
import type { BatchFormInfos } from 'state/batchForm/infos/infos.model';
import {
  areBatchFormsSelectedByFormIds,
  getBatchFormInfos,
  getBatchFormQuoteId,
  getExposedBatchFormStatus,
  getFormIdsInBatchTabIsNotError,
  isNoFormSelected as isNoFormSelectedByFormIds,
  selectBatchFormsState,
} from 'state/batchForm/infos/infos.selectors';
import { getBatchQuoteDetails } from 'state/batchForm/quoteDetails/quoteDetails.selectors';
import { getXMarginValue } from 'state/formData/formData.helper';
import type { DataById, XMarginData } from 'state/formData/formData.model';
import { getFormDataSnapshot } from 'state/formData/formData.selectors';
import { ExposedFormStatus, FormStatus } from 'state/forms/models';
import type { QuoteInfos } from 'state/quotes/pendings/pendings.model';
import * as fromPending from 'state/quotes/pendings/pendings.selectors';
import * as fromQuoteResult from 'state/quotes/results/results.selectors';
import * as fromTrade from 'state/quotes/trades/trades.selectors';
import type { AppState } from 'state/state.model';
import { getFormIdsInBatchTab } from 'state/workspaces/batch/tabContent/tabContent.selectors';
import { getPercentageString } from 'utils/number';
import { isExternalQuoteId } from 'utils/string';

import {
  getFieldIdsWithNullValue,
  isOnBehalf,
  isPatchingCustomizedXMarginWithNullValue,
  isPatchingSolvingMode,
} from './helper';

// TODO: batch screen unit test
export function getBatchFormIdsInTabIsNotError(state: AppState, tabId: string) {
  const formIds = getFormIdsInBatchTab(state, tabId);
  return getFormIdsInBatchTabIsNotError(state, formIds);
}

export function areAllBatchFormsSelectedByTabId(state: AppState, tabId: string): boolean {
  const formIds = getFormIdsInBatchTab(state, tabId);
  return areBatchFormsSelectedByFormIds(state, formIds);
}

export function isNoFormSelectedByTabId(state: AppState, tabId: string): boolean {
  const formIds = getFormIdsInBatchTab(state, tabId);
  return isNoFormSelectedByFormIds(state, formIds);
}

export function getSelectedFormIdsInBatchTab(appState: AppState, tabId: string) {
  return getSelectedBatchFormsInBatchTab(appState, tabId).map(([id]) => id);
}

export function getAllSelectedFormsMatching(
  appState: AppState,
  tabId: string,
  predicate: (form: BatchFormInfos) => boolean,
) {
  const selectedForms = getSelectedBatchFormsInBatchTab(appState, tabId);
  const matchingForms = selectedForms.filter(([, form]) => form && predicate(form));
  return selectedForms.length > 0 && matchingForms.length === selectedForms.length ? matchingForms : [];
}

export function getSelectedBatchFormsInBatchTab(appState: AppState, tabId: string) {
  const formIds = getFormIdsInBatchTab(appState, tabId);
  return formIds
    .map(formId => [formId, getBatchFormInfos(appState, formId)] as const)
    .filter(([, formInfo]) => formInfo?.isSelected);
}

export function isAnyBatchFormMatchingStatus(state: AppState, tabId: string, status: FormStatus): boolean {
  return (
    getFormIdsInBatchTab(state, tabId)?.some(formId => getBatchFormInfos(state, formId)?.status === status) ?? false
  );
}

export function getNumberOfFormsSelectedInBatchTab(appState: AppState, tabId: string): number {
  return getSelectedFormIdsInBatchTab(appState, tabId).length;
}

export function getBatchQuoteInfos(state: AppState, formId: string): QuoteInfos | undefined {
  const quoteId = getBatchFormQuoteId(state, formId);
  return quoteId
    ? fromQuoteResult.getQuoteInfos(state.quotes.results, quoteId)
    : fromPending.getQuoteInfos(state.quotes.pendings, formId);
}

export function getBatchQuoteResult(state: AppState, formId: string) {
  const quoteId = getBatchFormQuoteId(state, formId);
  return quoteId ? fromQuoteResult.getQuoteResult(state.quotes.results, quoteId) : undefined;
}

export function getBatchQuoteResultWarning(state: AppState, formId: string) {
  const quoteResult = getBatchQuoteResult(state, formId);
  return quoteResult?.warnings;
}

export function getBatchQuoteEstimatedDurationInMilliseconds(state: AppState, formId: string) {
  const quoteResult = getBatchQuoteResult(state, formId);
  return quoteResult?.estimatedQuotingDurationInMillis;
}

export function getBatchTradeWay(state: AppState, formId: string) {
  const quoteResult = getBatchQuoteResult(state, formId);
  return quoteResult?.tradeWay;
}

export function getBatchQuotePricingStartDate(state: AppState, formId: string) {
  const quoteInfos = getBatchQuoteInfos(state, formId);
  if (quoteInfos?.quoteRequestStartingTimeInMillis) {
    return new Date(quoteInfos.quoteRequestStartingTimeInMillis);
  } else {
    return undefined;
  }
}

export function getBatchQuoteSolveResult(state: AppState, formId: string): string | undefined {
  const quoteResult = getBatchQuoteResult(state, formId);

  if (quoteResult && typeof quoteResult.solveResult === 'number' && quoteResult.solveResultFormat === 'Number') {
    return quoteResult.solveResult.toFixed(4);
  }

  if (quoteResult?.solveResult) {
    return getPercentageString(quoteResult.solveResult, 4);
  }
  return undefined;
}

export function getBatchQuoteCreatedAt(state: AppState, formId: string) {
  const quoteResult = getBatchQuoteResult(state, formId);
  return quoteResult?.createdAt;
}

export function getBatchResult(state: AppState, formId: string) {
  const quoteDetails = getBatchQuoteDetails(state, formId);
  const quoteResults = getBatchQuoteResult(state, formId);
  const quoteId = getBatchFormQuoteId(state, formId);
  const externalQuoteId =
    quoteResults?.quoteExternalId ??
    quoteDetails?.QuoteExternalId ??
    (quoteId && isExternalQuoteId(quoteId) ? quoteId : undefined);

  return {
    ...(externalQuoteId && { QuoteExternalId: externalQuoteId }),
    ...(quoteResults?.pricingTime && { PricingTime: quoteResults?.pricingTime }),
    ...(quoteDetails?.PricerRfqId && { TessaId: quoteDetails?.PricerRfqId }),
    ...(quoteDetails?.Greeks && { SpotAndGreeks: quoteDetails?.Greeks }),
    ...(quoteDetails?.FICCGreeks && { Sensitivities: quoteDetails?.FICCGreeks }),
  };
}

export function getBatchFormIdByQuoteId(state: AppState, quoteId: string): string | undefined {
  const batchFormInfosState = selectBatchFormsState(state);
  return Object.keys(batchFormInfosState).find(formId => quoteId === getBatchFormQuoteId(state, formId));
}

export function getEffectiveExposedBatchFormStatus(state: AppState, formId: string): ExposedFormStatus | undefined {
  const status = getExposedBatchFormStatus(state, formId);
  const warnings = getBatchQuoteResultWarning(state, formId);
  return isNonEmpty(warnings) && status === 'Success' ? 'Warning' : status;
}

export function willLockXMargin(state: AppState, formId: string, patch: DataById) {
  const formDataSnapshot = getFormDataSnapshot(state, formId, patch);

  const client = formDataSnapshot['SelectedOnBehalfClient'];
  const solvingMode = formDataSnapshot['SolvingMode'];
  const xMargin = formDataSnapshot['XMargin'] as XMarginData;
  const isSolvingForXMargin = solvingMode === 'XMargin';

  return isOnBehalf(client) && !isSolvingForXMargin && getXMarginValue(xMargin) === null;
}

export function getBatchAutoSolve(state: AppState, formId: string, patch: DataById): string | undefined {
  if (isPatchingSolvingMode(patch)) {
    return undefined;
  }

  const allSolvableFieldIds = getBatchAllSolvableFieldIds(state, formId);
  const formDataSnapshot = getFormDataSnapshot(state, formId, patch);

  if (
    isPatchingCustomizedXMarginWithNullValue(patch) &&
    getFieldIdsWithNullValue(formDataSnapshot, allSolvableFieldIds).length === 1
  ) {
    return 'XMargin';
  }

  const reducedSolvableFieldIds = willLockXMargin(state, formId, patch)
    ? allSolvableFieldIds.filter(id => id !== 'XMargin')
    : allSolvableFieldIds;

  const solvableFieldsWithNullValue = getFieldIdsWithNullValue(formDataSnapshot, reducedSolvableFieldIds);

  if (solvableFieldsWithNullValue.length === 1) {
    const fieldId = solvableFieldsWithNullValue[0];
    const solveBy = getBatchFieldSolveBy(state, formId, fieldId);
    return formDataSnapshot['SolvingMode'] === solveBy ? undefined : solveBy;
  }

  return undefined;
}

export function getBatchTradeInfos(state: AppState, formId: string) {
  const quoteId = getBatchFormQuoteId(state, formId);
  return quoteId ? fromTrade.getTradeInfos(state.quotes.trades, quoteId) : undefined;
}
